/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SparkLine';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { page as SamplePage } from '../../help/sample_pages/SparkLinePage';

const SparkLineManager: ComponentManagerFn<'SparkLine'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SparkLine'>(platform, 'SparkLine'),
    type: 'SparkLine',
    name: 'Spark Line',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => {
          return ['Integer', 'Decimal', 'Double', 'Percent'];
        },
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'It is a column through which you can get the data from DataSource.',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      chartWidth: {
        category: 'General',
        label: 'Chart Width',
        type: 'number',
        max: 1000,
      },
      chartHeight: {
        category: 'General',
        label: 'Chart Height',
        type: 'number',
        max: 1000,
      },

      caption: {
        category: 'General',
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
      },
      captionFont: {
        category: 'General',
        label: 'Caption Font',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Font family for the chart caption.',
      },
      captionFontSize: {
        category: 'General',
        label: 'Caption Font Size',
        type: 'number',
        helpText: 'Font size for the chart caption.',
      },
      captionFontColor: {
        category: 'General',
        label: 'Caption Font Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the chart caption text.',
      },
      captionFontBold: {
        category: 'General',
        label: 'Caption Font Bold',
        type: 'boolean',
        helpText: 'Whether to make the caption text bold.',
      },
      subCaption: {
        category: 'General',
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
      },
      subCaptionFont: {
        category: 'General',
        label: 'Sub-Caption Font',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Font family for the chart sub-caption.',
      },
      subcaptionFontSize: {
        category: 'General',
        label: 'Sub-Caption Font Size',
        type: 'number',
        helpText: 'Font size for the chart sub-caption.',
      },
      subcaptionFontColor: {
        category: 'General',
        label: 'Sub-Caption Font Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the sub-caption text.',
      },
      subcaptionFontBold: {
        category: 'General',
        label: 'Sub-Caption Font Bold',
        type: 'boolean',
        helpText: 'Make sub-caption text bold.',
      },
      bgColor: {
        category: 'General',
        label: 'Background Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify the hex code for Background color for the chart.',
      },

      periodlength: {
        category: 'Period',
        label: 'Period Length',
        type: 'number',
        helpText: 'Length of each period in the sparkline.',
      },
      periodcolor: {
        category: 'Period',
        label: 'Period Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for period divisions.',
      },
      periodalpha: {
        category: 'Period',
        label: 'Period Alpha',
        type: 'number',
        helpText: 'Transparency level for period divisions (0-100).',
      },

      drawAnchor: {
        category: 'Data Points',
        label: 'Show Anchors',
        type: 'select',
        options: toOptions(['Hide All', 'Show All', 'Default']),
        helpText: 'Show/hide anchors on data points.',
      },
      showOpenAnchor: {
        category: 'Data Points',
        label: 'Show Open Anchor',
        type: 'boolean',
        helpText: 'Show/hide open value anchors.',
      },
      showCloseAnchor: {
        category: 'Data Points',
        label: 'Show Close Anchor',
        type: 'boolean',
        helpText: 'Show/hide close value anchors.',
      },
      showHighAnchor: {
        category: 'Data Points',
        label: 'Show High Anchor',
        type: 'boolean',
        helpText: 'Show/hide high value anchors.',
      },
      showLowAnchor: {
        category: 'Data Points',
        label: 'Show Low Anchor',
        type: 'boolean',
        helpText: 'Show/hide low value anchors.',
      },

      lineColor: {
        category: 'Line',
        label: 'Line Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the sparkline.',
      },
      lineThickness: {
        category: 'Line',
        label: 'Line Thickness',
        type: 'number',
        helpText: 'Thickness of the sparkline.',
      },
      lineAlpha: {
        category: 'Line',
        label: 'Line Alpha',
        type: 'number',
        helpText: 'Transparency level for the line (0-100).',
      },

      anchorRadius: {
        category: 'Data Points',
        label: 'Anchor Radius',
        type: 'number',
        helpText: 'Radius of anchor points.',
      },
      anchorAlpha: {
        category: 'Data Points',
        label: 'Anchor Alpha',
        type: 'number',
        helpText: 'Transparency level for anchors (0-100).',
      },

      showTooltip: {
        category: 'Interactivity',
        label: 'Show Tooltip',
        type: 'boolean',
        helpText: 'Show/hide tooltips on hover.',
      },
      plotToolText: {
        category: 'Interactivity',
        label: 'Plot Tool Text',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Custom tooltip text format.',
      },
      showTrendLine: {
        category: 'Trend Lines',
        label: 'Show Trend Line',
        type: 'boolean',
        helpText: 'Turn this on to show Trend Line',
      },
      startValue: {
        category: 'Trend Lines',
        label: 'Start Value',
        type: 'number',
        helpText: 'Starting point for the trendline',
      },
      endValue: {
        category: 'Trend Lines',
        label: 'End Value',
        type: 'number',
        helpText: 'End point for the trendline',
      },
      thickness: {
        category: 'Trend Lines',
        label: 'Thickenss',
        type: 'number',
        helpText: 'Thickness for the trendline',
      },
      alpha: {
        category: 'Trend Lines',
        label: 'Alpha',
        type: 'number',
        helpText: 'Transparency level for Trendline (0-100).',
      },
      color: {
        category: 'Trend Lines',
        label: 'Trend Line Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Color for the trendline.',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    isPropertyVisible(prop, itemProps): boolean {
      switch (prop as string) {
        case 'plotToolText':
          return itemProps.showTooltip;
        case 'startValue':
        case 'endValue':
        case 'alpha':
        case 'color':
        case 'thickness':
          return itemProps.showTrendLine;
        case 'valueAttribute':
          return itemProps.dsalias !== '';
        default:
          return true;
      }
    },
    category: 'Fusion Charts',
    icon: { iconName: 'chart-mixed' },
    description: `**Overview**<br/>
The Spark Line component is a compact, minimalistic chart designed to visualize trends and variations in data without the visual clutter of axes, grids, or detailed labels. Based on the FusionCharts library, it provides a "word-sized" graphic that fits neatly into data grids, dashboards, or text-heavy reports.
Use this component to provide quick context to a standalone number—for example, placing a Spark Line next to a "Current Stock Price" to show its performance over the last 30 days.

**1. Data Connection**<br/>
The Spark Line requires a direct connection to a data source to render the trend.
* **DataSource Alias (dsalias):** Selects the Data Store (Cloud, Local, or ReadOnly) containing the dataset.
* **Value Attribute:** The specific numeric column to visualize.
* **Supported Types:** **Integer**, **Decimal**, **Double**, **Percent**.
* **Do Not Prefetch:** If enabled, the chart waits to load data until explicitly triggered, optimizing performance for pages containing many charts.

**2. Visual Configuration**<br/>
You can customize the line style and surrounding elements to fit your UI theme.
* **Dimensions:** Control **Chart Width** and **Chart Height** (Max 1000px).
* **Line Style:** Customize the **Line Color** (Expression), **Line Thickness**, and **Line Alpha** (transparency).
* **Captions:** Add a **Caption** and **Sub-Caption** with full control over font family, size, color, and weight (**Bold**).
* **Period Blocks:** Use **Period Length** to visually divide the chart into blocks (e.g., set to 7 to visualize weekly segments) and style them with **Period Color** and **Alpha**.

**3. Anchors & Indicators**<br/>
Anchors are dots placed on the line to highlight specific data points. You can toggle them globally or for specific values.
* **Draw Anchor:** Global control to **Show All**, **Hide All**, or use defaults.
* **Special Anchors:** Specific boolean toggles to highlight key milestones:
    * **Show High/Low Anchor:** Highlights the maximum and minimum values in the series.
    * **Show Open/Close Anchor:** Highlights the first and last values in the series.
* **Styling:** Control **Anchor Radius** and **Anchor Alpha** to make points subtle or prominent.

**4. Trend Lines**<br/>
You can overlay a static reference line to provide context, such as a target or an average.
* **Show Trend Line:** Boolean toggle to enable the reference line.
* **Range:** Defined by **Start Value** and **End Value**.
* **Appearance:** Customized via **Color**, **Thickness**, and **Alpha**.

**5. Interactivity**<br/>
While minimalistic, the Spark Line supports basic user interaction.
* **Tooltips:** Enable **Show Tooltip** to display values when hovering over the line. You can customize the message using **Plot Tool Text** (e.g., "Value: $dataValue").
* **Triggers:** Supports the **TriggerOnClick** child component, allowing the entire chart to act as a button (e.g., clicking the sparkline opens a detailed analysis page).

**6. Best Use Cases**<br/>
* **Financial Dashboards:** Displaying stock price history next to the current ticker value.
* **Performance KPIs:** Showing "Server Load" or "Memory Usage" trends over the last hour.
* **Sales Reports:** Embedding a 12-month sales trend column inside a data grid row for each product.
* **Climate Data:** Visualizing temperature fluctuations over a week.`,
    helpRenders: [
      {
        title: 'Basic SparkLine',
        description:
          'A simple sparkline chart showing data trends without axes.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 200,
        visibleProps: ['caption', 'subCaption', 'lineColor', 'lineThickness'],
        item: {
          children: [],
          itemId: 'sparkLine',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'someNumber',
            caption: {
              type: 'text',
              value: 'Stock Price',
            },
            subCaption: {
              type: 'text',
              value: 'Last 30 Days',
            },
            lineColor: {
              type: 'text',
              value: '#6baa01',
            },
            lineThickness: 2,
          },
          type: 'SparkLine',
        },
      },
      {
        title: 'SparkLine with Anchors',
        description:
          'Display data points with visible anchors highlighting key values.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        height: 200,
        visibleProps: [
          'drawAnchor',
          'showOpenAnchor',
          'showCloseAnchor',
          'showHighAnchor',
          'showLowAnchor',
          'anchorRadius',
        ],
        item: {
          children: [],
          itemId: 'sparkLine',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'someNumber',
            caption: {
              type: 'text',
              value: 'Revenue Trend',
            },
            drawAnchor: 'Show All',
            showOpenAnchor: true,
            showCloseAnchor: true,
            showHighAnchor: true,
            showLowAnchor: true,
            anchorRadius: 3,
            lineColor: {
              type: 'text',
              value: '#0066cc',
            },
          },
          type: 'SparkLine',
        },
      },
      {
        title: 'SparkLine with Trendline',
        description: 'Add a trendline to show target or average values.',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        height: 200,
        visibleProps: [
          'showTrendLine',
          'startValue',
          'endValue',
          'color',
          'thickness',
          'alpha',
        ],
        item: {
          children: [],
          itemId: 'sparkLine',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'someNumber',
            caption: {
              type: 'text',
              value: 'Sales Performance',
            },
            subCaption: {
              type: 'text',
              value: 'With Target Line',
            },
            showTrendLine: true,
            startValue: 35,
            endValue: 45,
            color: {
              type: 'text',
              value: '#ff0000',
            },
            thickness: 2,
            alpha: 80,
            lineColor: {
              type: 'text',
              value: '#29C3BE',
            },
          },
          type: 'SparkLine',
        },
      },
      {
        title: 'Customized SparkLine',
        description:
          'Fully customized sparkline with period blocks and styling.',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        height: 200,
        visibleProps: [
          'bgColor',
          'periodlength',
          'periodcolor',
          'periodalpha',
          'captionFontSize',
          'captionFontBold',
        ],
        item: {
          children: [],
          itemId: 'sparkLine',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'someNumber',
            caption: {
              type: 'text',
              value: 'Weekly Analysis',
            },
            captionFontSize: 14,
            captionFontBold: true,
            bgColor: {
              type: 'text',
              value: '#f5f5f5',
            },
            periodlength: 7,
            periodcolor: {
              type: 'text',
              value: '#cccccc',
            },
            periodalpha: 20,
            lineColor: {
              type: 'text',
              value: '#5D62B5',
            },
            lineThickness: 2,
            drawAnchor: 'Default',
          },
          type: 'SparkLine',
        },
      },
      {
        title: 'SparkLine with Tooltip',
        description:
          'Display custom tooltips on hover for better data insights.',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        height: 200,
        visibleProps: ['showTooltip', 'plotToolText', 'lineAlpha'],
        item: {
          children: [],
          itemId: 'sparkLine',
          parent: 'flexItem1',
          props: {
            dsalias: 'test',
            valueAttribute: 'someNumber',
            caption: {
              type: 'text',
              value: 'Interactive Chart',
            },
            showTooltip: true,
            plotToolText: {
              type: 'text',
              value: 'Value: $dataValue',
            },
            lineColor: {
              type: 'text',
              value: '#F2726F',
            },
            lineAlpha: 85,
            drawAnchor: 'Show All',
            anchorRadius: 4,
          },
          type: 'SparkLine',
        },
      },
    ],
  };
};
export default SparkLineManager;
